import { TreeItem } from '@graftcode/design-system'

/**
 * Recursively filters tree items based on a search query.
 * Returns items that match the query or have children that match.
 * When a match is found, all ancestors are included to show the path.
 *
 * @param items - Array of tree items to filter
 * @param query - Search query string (case-insensitive)
 * @returns Filtered array of tree items with matching items and their ancestors
 */
export function filterTreeItems(items: TreeItem[], query: string): TreeItem[] {
  if (!query.trim()) {
    return items
  }

  const searchQuery = query.toLowerCase().trim()

  const matchesQuery = (item: TreeItem): boolean => item.label?.toLowerCase().includes(searchQuery) ?? false

  const filterItem = (item: TreeItem): TreeItem | null => {
    const itemMatches = matchesQuery(item)

    let filteredChildren: TreeItem[] | undefined
    if (item.children) {
      filteredChildren = item.children.map(filterItem).filter((child): child is TreeItem => child !== null)
    }

    if (itemMatches || (filteredChildren && filteredChildren.length > 0)) {
      return {
        ...item,
        children: filteredChildren && filteredChildren.length > 0 ? filteredChildren : undefined,
      }
    }

    return null
  }

  return items.map(filterItem).filter((item): item is TreeItem => item !== null)
}

/**
 * Collects all IDs of items that should be expanded when search is active.
 * This includes all ancestors of matching items to show the full path.
 *
 * @param items - Array of tree items
 * @param query - Search query string (case-insensitive)
 * @returns Array of item IDs that should be expanded
 */
export function getExpandedIdsForSearch(items: TreeItem[], query: string): string[] {
  if (!query.trim()) {
    return []
  }

  const searchQuery = query.toLowerCase().trim()
  const expandedIds: string[] = []

  const collectExpandedIds = (item: TreeItem, parentPath: string[] = []): boolean => {
    const currentPath = [...parentPath, item.id]
    const itemMatches = item.label?.toLowerCase().includes(searchQuery) ?? false

    let hasMatchingChildren = false
    if (item.children) {
      hasMatchingChildren = item.children.some((child) => collectExpandedIds(child, currentPath))
    }

    if (itemMatches || hasMatchingChildren) {
      currentPath.forEach((id) => {
        if (!expandedIds.includes(id)) {
          expandedIds.push(id)
        }
      })
      return true
    }

    return false
  }

  items.forEach((item) => {
    collectExpandedIds(item)
  })

  return expandedIds
}
